import { type ReactElement, type ReactNode } from 'react'
import {
  getName,
  isDefined,
  isEmpty,
  isNotDefined,
  type TinyUser,
  type UserId,
  type Workflow,
  type WorkflowActionId,
  type WorkflowStageId,
} from '@terros/common'
import { Tooltip } from '@mantine/core'
import { useTinyUser } from '@front/model'
import { PrefetchedUserInfo } from '@components'
import { WorkflowIconDisplay } from '../../terros/icon/WorkflowIcons'
import { type IconDisplaySize } from '../../terros/icon/types'
import { getWorkflowStageIconEntry, toPinDisplayProps } from '../../map/workflowStageIcon'
import { ResultTypeIcon } from './ResultTypeIcon'

export const AccountStageActionIcon = ({
  account,
  workflow,
  companyIconUrl,
  size,
}: {
  account: { workflowStageId?: WorkflowStageId; workflowActionId?: WorkflowActionId }
  workflow: Workflow
  companyIconUrl?: string
  size?: IconDisplaySize
}): ReactNode => {
  const entry = getWorkflowStageIconEntry(account, workflow, companyIconUrl)
  return <WorkflowIconDisplay {...toPinDisplayProps(entry)} size={size} />
}

type AccountResultStageIconProps = {
  stageIcon: ReactNode
  stageName: string | undefined
}

export const AccountResultStageIcon = ({ stageIcon, stageName }: AccountResultStageIconProps): ReactNode => {
  if (isNotDefined(stageIcon)) return <ResultTypeIcon icon='home' />
  if (isEmpty(stageName)) return stageIcon
  return (
    <Tooltip label={stageName}>
      <div>{stageIcon}</div>
    </Tooltip>
  )
}

type AccountOwnerAvatarProps = {
  account: { ownerId?: UserId; owner?: Omit<TinyUser, 'userId'> }
  /** prepended to the owner's name in the tooltip */
  tooltipPrefix?: string
}

export const AccountOwnerAvatar = ({ account, tooltipPrefix = '' }: AccountOwnerAvatarProps): ReactNode => {
  if (isNotDefined(account.ownerId)) return
  if (isDefined(account.owner)) {
    return <OwnerAvatar owner={{ ...account.owner, userId: account.ownerId }} tooltipPrefix={tooltipPrefix} />
  }
  // some account payloads only carry ownerId, so load the owner
  return <FetchedOwnerAvatar userId={account.ownerId} tooltipPrefix={tooltipPrefix} />
}

const FetchedOwnerAvatar = ({ userId, tooltipPrefix }: { userId: UserId; tooltipPrefix: string }): ReactNode => {
  const { data: owner } = useTinyUser(userId)
  if (isNotDefined(owner)) return
  return <OwnerAvatar owner={owner} tooltipPrefix={tooltipPrefix} />
}

const OwnerAvatar = ({ owner, tooltipPrefix }: { owner: TinyUser; tooltipPrefix: string }): ReactElement => (
  <Tooltip label={`${tooltipPrefix}${getName(owner)}`}>
    <div>
      <PrefetchedUserInfo avatarOnly linked={false} user={owner} />
    </div>
  </Tooltip>
)
